import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  List as ListIcon,
  Plus,
  SlidersHorizontal,
  ThumbsDown,
  ThumbsUp,
  X,
} from 'lucide-react';
import type {
  CalendarEntry,
  CalendarEntryType,
  ClubEventResponse,
  SeasonMatchResponse,
  TeamResponse,
  TrainingGroupResponse,
  VotingOverview,
} from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { useLockBodyScroll } from '../lib/use-lock-body-scroll';
import { responseButtonStyle } from '../lib/response-button-style';
import { Alert, Button, Card, Field, Input } from '../components/ui';
import { DateBadge } from '../components/DateBadge';
import { RosterModal, type RosterGroup } from '../components/RosterModal';
import { TrainingRespondModal } from './TrainingPage';

// ── date helpers (pure, no date library — see docs/calendar.md) ────────────
const DAY_MS = 86_400_000;
const WEEKDAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTH_LABELS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function parseIso(s: string): Date {
  const [y, m, day] = s.split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, day!));
}
function addDays(d: Date, n: number): Date {
  return new Date(d.getTime() + n * DAY_MS);
}
function addMonths(d: Date, n: number): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1));
}
/** Monday of the week containing `d`. */
function startOfWeek(d: Date): Date {
  const wd = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
  return addDays(d, -(wd - 1));
}
function startOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}
function startOfYear(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
}

type GridScale = 'month' | 'week';
type ListScale = 'week' | 'month' | 'year';

const ENTRY_TYPE_LABEL: Record<CalendarEntryType, string> = {
  match: 'Saisonspiel',
  training: 'Training',
  club_event: 'Vereinstermin',
};
const ENTRY_TYPE_DOT: Record<CalendarEntryType, string> = {
  match: 'bg-violet-500',
  training: 'bg-blue-500',
  club_event: 'bg-amber-500',
};
const ENTRY_TYPE_CHIP: Record<CalendarEntryType, string> = {
  match: 'bg-violet-50 text-violet-700',
  training: 'bg-blue-50 text-blue-700',
  club_event: 'bg-amber-50 text-amber-700',
};
/** Linker Akzentstreifen je Termin-Typ in der Listenansicht (§ Nutzeranfrage
 * „Listenansicht … professionell und modern … immer noch unübersichtlich") —
 * dieselbe Farbfamilie wie `ENTRY_TYPE_DOT`/`ENTRY_TYPE_CHIP`, nur als
 * durchgehende Kante statt eines kaum sichtbaren 8px-Punkts, damit der Typ
 * eines Termins auch beim schnellen Scrollen auf einen Blick erkennbar ist. */
const ENTRY_TYPE_BORDER: Record<CalendarEntryType, string> = {
  match: 'border-violet-400',
  training: 'border-blue-400',
  club_event: 'border-amber-400',
};
const ALL_TYPES: CalendarEntryType[] = ['match', 'training', 'club_event'];

function statusLabel(status: CalendarEntry['status']): string | null {
  switch (status) {
    case 'cancelled':
      return 'Abgesagt';
    case 'postponed':
      return 'Verlegt';
    default:
      return null;
  }
}

export function CalendarPage() {
  const { can } = useAuth();
  const qc = useQueryClient();

  // § Nutzeranfrage „Listenansicht soll die Standardansicht … auf der
  // mobilen Ansicht werden" — einmalig beim Mount anhand der Viewport-Breite
  // entschieden (derselbe `md`-Breakpoint, 768px, wie überall sonst in der
  // App), nicht reaktiv auf spätere Fenstergrößenänderungen: ein Nutzer, der
  // die Ansicht bewusst umschaltet, soll dabei bleiben, auch wenn sich z. B.
  // die Bildschirmausrichtung ändert. Auf dem Desktop bleibt „Kalender"
  // (Monatsraster) weiterhin der Standard — dort ist dafür Platz.
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => (window.innerWidth < 768 ? 'list' : 'grid'));
  const [gridScale, setGridScale] = useState<GridScale>('month');
  const [listScale, setListScale] = useState<ListScale>('month');
  const [anchor, setAnchor] = useState<Date>(() => startOfMonth(new Date()));

  const [activeTypes, setActiveTypes] = useState<Set<CalendarEntryType>>(new Set(ALL_TYPES));
  const [teamId, setTeamId] = useState('');
  const [trainingGroupId, setTrainingGroupId] = useState('');
  const [mine, setMine] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editEventId, setEditEventId] = useState<string | null>(null);
  // § Nutzeranfrage „auf die Termine klicken … Teilnehmer Pop-up … an- und
  // abmelden" — ersetzt das frühere Wegnavigieren zur Trainings-/Saison-Seite
  // (siehe `handlePick` unten) durch dasselbe Pop-up, das dort auch beim
  // direkten Klick auf die Karte erscheint.
  const [respondEntry, setRespondEntry] = useState<{ type: 'match' | 'training'; sourceId: string } | null>(null);
  // Filters collapsed by default on mobile — type pills + 2 selects + a
  // checkbox took up most of a phone screen's height before any actual
  // calendar/list content appeared. Always visible on desktop (`md:flex`
  // below), where there's room.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilterCount =
    (activeTypes.size < ALL_TYPES.length ? 1 : 0) + (teamId ? 1 : 0) + (trainingGroupId ? 1 : 0) + (mine ? 1 : 0);

  const scale = viewMode === 'grid' ? gridScale : listScale;
  const { from, to, label } = useMemo(() => range(anchor, scale), [anchor, scale]);

  const { data: teamsData } = useQuery({
    queryKey: ['teams', 'all'],
    queryFn: () => api<{ data: TeamResponse[] }>('/teams?pageSize=200', { orgScoped: true }),
  });
  const { data: groups } = useQuery({
    queryKey: ['training', 'groups'],
    queryFn: () => api<TrainingGroupResponse[]>('/training/groups', { orgScoped: true }),
  });

  const filterKey = [...activeTypes].sort().join(',') + `|${teamId}|${trainingGroupId}|${mine}`;
  const { data: entries, isLoading } = useQuery({
    queryKey: ['calendar', from, to, filterKey],
    queryFn: () => {
      const params = new URLSearchParams({ from, to });
      if (activeTypes.size < ALL_TYPES.length) params.set('types', [...activeTypes].join(','));
      if (teamId) params.set('teamIds', teamId);
      if (trainingGroupId) params.set('trainingGroupIds', trainingGroupId);
      if (mine) params.set('mine', 'true');
      return api<CalendarEntry[]>(`/calendar?${params.toString()}`, { orgScoped: true });
    },
  });

  const entriesByDate = useMemo(() => {
    const m = new Map<string, CalendarEntry[]>();
    for (const e of entries ?? []) m.set(e.date, [...(m.get(e.date) ?? []), e]);
    return m;
  }, [entries]);

  function toggleType(t: CalendarEntryType) {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next.size === 0 ? new Set(ALL_TYPES) : next;
    });
  }

  /** Shared between the always-visible desktop filter row and the
   * collapsible mobile panel — `stacked` widens the selects to fill the
   * narrower mobile panel instead of sizing to content. */
  function renderFilters(stacked = false) {
    return (
      <>
        <div className="flex flex-wrap gap-1.5">
          {ALL_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => toggleType(t)}
              className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition ${
                activeTypes.has(t)
                  ? `${ENTRY_TYPE_CHIP[t]} border-transparent`
                  : 'border-slate-200 text-slate-400 hover:border-slate-300'
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${ENTRY_TYPE_DOT[t]}`} />
              {ENTRY_TYPE_LABEL[t]}
            </button>
          ))}
        </div>
        <select
          value={teamId}
          onChange={(e) => setTeamId(e.target.value)}
          className={`rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-700 ${stacked ? 'w-full' : ''}`}
        >
          <option value="">Alle Mannschaften</option>
          {teamsData?.data.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <select
          value={trainingGroupId}
          onChange={(e) => setTrainingGroupId(e.target.value)}
          className={`rounded-md border border-slate-300 px-2 py-1 text-sm text-slate-700 ${stacked ? 'w-full' : ''}`}
        >
          <option value="">Alle Trainingsgruppen</option>
          {groups?.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
          Nur meine Termine
        </label>
      </>
    );
  }

  function step(dir: 1 | -1) {
    setAnchor((a) => {
      if (scale === 'month') return addMonths(a, dir);
      if (scale === 'year') return new Date(Date.UTC(a.getUTCFullYear() + dir, 0, 1));
      return addDays(a, dir * 7);
    });
  }

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['calendar'] });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="hidden text-lg font-semibold text-slate-800 md:block">Kalender</h1>
          <p className="text-sm text-slate-500">Saisonspiele, Training und Vereinstermine an einem Ort.</p>
        </div>
        {can('calendar.manage') && (
          <Button onClick={() => setCreateOpen(true)} className="gap-1.5">
            <Plus size={16} /> Termin anlegen
          </Button>
        )}
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-slate-300">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${viewMode === 'grid' ? 'bg-brand-accent text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              <CalendarDays size={14} /> Kalender
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 border-l border-slate-300 px-3 py-1.5 text-sm ${viewMode === 'list' ? 'bg-brand-accent text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              <ListIcon size={14} /> Liste
            </button>
          </div>

          {viewMode === 'grid' ? (
            <div className="flex overflow-hidden rounded-md border border-slate-300 text-sm">
              {(['month', 'week'] as GridScale[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setGridScale(s)}
                  className={`px-3 py-1.5 ${gridScale === s ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                >
                  {s === 'month' ? 'Monat' : 'Woche'}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex overflow-hidden rounded-md border border-slate-300 text-sm">
              {(['week', 'month', 'year'] as ListScale[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setListScale(s)}
                  className={`px-3 py-1.5 ${listScale === s ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                >
                  {s === 'week' ? 'Woche' : s === 'month' ? 'Monat' : 'Jahr'}
                </button>
              ))}
            </div>
          )}

          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => step(-1)}
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
              aria-label="Zurück"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => setAnchor(scale === 'month' ? startOfMonth(new Date()) : scale === 'year' ? startOfYear(new Date()) : startOfWeek(new Date()))}
              className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              Heute
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
              aria-label="Weiter"
            >
              <ChevronRight size={18} />
            </button>
            <span className="ml-1 min-w-[9rem] text-sm font-medium text-slate-700">{label}</span>
          </div>
        </div>

        {/* Desktop: filters always visible, same as before. */}
        <div className="mt-3 hidden flex-wrap items-center gap-3 border-t border-slate-100 pt-3 md:flex">
          {renderFilters()}
        </div>

        {/* Mobile: collapsed behind a toggle so the actual calendar/list
         * content isn't pushed below the fold by four rarely-changed
         * filter controls. */}
        <div className="mt-3 md:hidden">
          <button
            type="button"
            onClick={() => setFiltersOpen((o) => !o)}
            className="flex w-full items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-600"
          >
            <span className="flex items-center gap-1.5">
              <SlidersHorizontal size={14} />
              Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </span>
            <ChevronDown size={16} className={filtersOpen ? 'rotate-180 transition-transform' : 'transition-transform'} />
          </button>
          {filtersOpen && (
            <div className="mt-2 flex flex-col gap-2.5 rounded-md border border-slate-100 bg-slate-50 p-3">
              {renderFilters(true)}
            </div>
          )}
        </div>
      </Card>

      {isLoading && <p className="text-sm text-slate-400">Lädt…</p>}

      {viewMode === 'grid' && gridScale === 'month' && (
        <MonthGrid anchor={anchor} entriesByDate={entriesByDate} onPick={(e) => handlePick(e)} />
      )}
      {viewMode === 'grid' && gridScale === 'week' && (
        <WeekGrid anchor={anchor} entriesByDate={entriesByDate} onPick={(e) => handlePick(e)} />
      )}
      {viewMode === 'list' && (
        <AgendaList from={from} to={to} entriesByDate={entriesByDate} isLoading={isLoading} onPick={(e) => handlePick(e)} />
      )}

      {createOpen && (
        <ClubEventEditor
          teams={teamsData?.data ?? []}
          groups={groups ?? []}
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            invalidate();
          }}
        />
      )}
      {editEventId && (
        <ClubEventEditor
          eventId={editEventId}
          teams={teamsData?.data ?? []}
          groups={groups ?? []}
          onClose={() => setEditEventId(null)}
          onSaved={() => {
            setEditEventId(null);
            invalidate();
          }}
        />
      )}

      {respondEntry?.type === 'training' && (
        <TrainingRespondModal
          eventId={respondEntry.sourceId}
          onClose={() => setRespondEntry(null)}
          onChanged={invalidate}
        />
      )}
      {respondEntry?.type === 'match' && (
        <CalendarMatchPopup
          matchId={respondEntry.sourceId}
          onClose={() => setRespondEntry(null)}
          onChanged={invalidate}
        />
      )}
    </div>
  );

  // Matches/Training open the same Teilnehmer-Pop-up as their own page (§
  // Nutzeranfrage) — gated by the same permission that page itself requires,
  // so a click that the API would reject anyway (403) silently does nothing
  // rather than opening a popup that only ever errors. Club events open the
  // editor directly, if the caller can manage them — otherwise just show
  // what's already on the card, nothing to open.
  function handlePick(entry: CalendarEntry) {
    if (entry.type === 'match') {
      if (can('season.view')) setRespondEntry({ type: 'match', sourceId: entry.sourceId });
    } else if (entry.type === 'training') {
      if (can('training.view')) setRespondEntry({ type: 'training', sourceId: entry.sourceId });
    } else if (entry.canManage) setEditEventId(entry.sourceId);
  }
}

function range(anchor: Date, scale: GridScale | ListScale): { from: string; to: string; label: string } {
  if (scale === 'week') {
    const start = startOfWeek(anchor);
    const end = addDays(start, 6);
    const label =
      start.getUTCMonth() === end.getUTCMonth()
        ? `${start.getUTCDate()}.–${end.getUTCDate()}. ${MONTH_LABELS[end.getUTCMonth()]} ${end.getUTCFullYear()}`
        : `${start.getUTCDate()}. ${MONTH_LABELS[start.getUTCMonth()]} – ${end.getUTCDate()}. ${MONTH_LABELS[end.getUTCMonth()]} ${end.getUTCFullYear()}`;
    return { from: isoDate(start), to: isoDate(end), label };
  }
  if (scale === 'year') {
    const start = startOfYear(anchor);
    const end = new Date(Date.UTC(start.getUTCFullYear(), 11, 31));
    return { from: isoDate(start), to: isoDate(end), label: String(start.getUTCFullYear()) };
  }
  const start = startOfMonth(anchor);
  const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0));
  return { from: isoDate(start), to: isoDate(end), label: `${MONTH_LABELS[start.getUTCMonth()]} ${start.getUTCFullYear()}` };
}

function EntryPill({ entry, onPick }: { entry: CalendarEntry; onPick: (e: CalendarEntry) => void }) {
  const struck = entry.status === 'cancelled';
  const sl = statusLabel(entry.status);
  // § Nutzeranfrage „angemeldet … grün schimmern … abgemeldet … rot" — die
  // eigene Rückmeldung überdeckt hier die sonst typ-abhängige Farbe
  // (`ENTRY_TYPE_CHIP`), weil sie für den Nutzer in diesem Moment die
  // relevantere Information ist als "ist das ein Training oder ein Spiel".
  const ownTint =
    entry.ownResponse === 'registered'
      ? 'bg-green-100 text-green-800'
      : entry.ownResponse === 'declined'
        ? 'bg-red-100 text-red-700'
        : ENTRY_TYPE_CHIP[entry.type];
  return (
    <button
      type="button"
      onClick={() => onPick(entry)}
      title={`${entry.title}${entry.startTime ? ` · ${entry.startTime.slice(0, 5)}` : ''}${sl ? ` · ${sl}` : ''}`}
      className={`block w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] font-medium ${ownTint} ${struck ? 'line-through opacity-60' : ''}`}
    >
      {entry.startTime && <span className="opacity-70">{entry.startTime.slice(0, 5)} </span>}
      {entry.title}
    </button>
  );
}

function MonthGrid({
  anchor,
  entriesByDate,
  onPick,
}: {
  anchor: Date;
  entriesByDate: Map<string, CalendarEntry[]>;
  onPick: (e: CalendarEntry) => void;
}) {
  const start = startOfMonth(anchor);
  const gridStart = startOfWeek(start);
  const month = start.getUTCMonth();
  const weeks: Date[][] = [];
  let cursor = gridStart;
  for (let w = 0; w < 6; w++) {
    const week: Date[] = [];
    for (let d = 0; d < 7; d++) {
      week.push(cursor);
      cursor = addDays(cursor, 1);
    }
    weeks.push(week);
    if (cursor.getUTCMonth() !== month && cursor > start) break;
  }
  const todayIso = isoDate(new Date());

  return (
    <Card>
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-md bg-slate-200 text-xs">
        {WEEKDAY_LABELS.map((wd) => (
          <div key={wd} className="bg-slate-50 px-2 py-1.5 text-center font-medium text-slate-500">
            {wd}
          </div>
        ))}
        {weeks.flat().map((d) => {
          const iso = isoDate(d);
          const dayEntries = entriesByDate.get(iso) ?? [];
          const inMonth = d.getUTCMonth() === month;
          const isToday = iso === todayIso;
          return (
            <div key={iso} className={`min-h-[6.5rem] bg-white p-1 ${inMonth ? '' : 'bg-slate-50/60'}`}>
              <div
                className={`mb-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${
                  isToday ? 'bg-brand-accent font-semibold text-white' : inMonth ? 'text-slate-600' : 'text-slate-300'
                }`}
              >
                {d.getUTCDate()}
              </div>
              <div className="space-y-0.5">
                {dayEntries.slice(0, 3).map((e) => (
                  <EntryPill key={e.id} entry={e} onPick={onPick} />
                ))}
                {dayEntries.length > 3 && (
                  <div className="px-1.5 text-[10px] text-slate-400">+{dayEntries.length - 3} weitere</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function WeekGrid({
  anchor,
  entriesByDate,
  onPick,
}: {
  anchor: Date;
  entriesByDate: Map<string, CalendarEntry[]>;
  onPick: (e: CalendarEntry) => void;
}) {
  const start = startOfWeek(anchor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const todayIso = isoDate(new Date());

  return (
    <Card>
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-md bg-slate-200 text-xs">
        {days.map((d) => {
          const iso = isoDate(d);
          const dayEntries = entriesByDate.get(iso) ?? [];
          const isToday = iso === todayIso;
          return (
            <div key={iso} className="min-h-[16rem] bg-white p-1.5">
              <div className={`mb-1.5 text-center ${isToday ? 'font-semibold text-brand-accent' : 'text-slate-500'}`}>
                <div className="text-[10px] uppercase">{WEEKDAY_LABELS[d.getUTCDay() === 0 ? 6 : d.getUTCDay() - 1]}</div>
                <div className="text-sm">{d.getUTCDate()}</div>
              </div>
              <div className="space-y-1">
                {dayEntries.map((e) => (
                  <EntryPill key={e.id} entry={e} onPick={onPick} />
                ))}
                {dayEntries.length === 0 && <div className="text-center text-[10px] text-slate-300">–</div>}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function AgendaList({
  from,
  to,
  entriesByDate,
  isLoading,
  onPick,
}: {
  from: string;
  to: string;
  entriesByDate: Map<string, CalendarEntry[]>;
  isLoading: boolean;
  onPick: (e: CalendarEntry) => void;
}) {
  const dates: string[] = [];
  for (let d = parseIso(from); isoDate(d) <= to; d = addDays(d, 1)) dates.push(isoDate(d));
  const nonEmpty = dates.filter((d) => (entriesByDate.get(d)?.length ?? 0) > 0);

  // § Nutzeranfrage „heutiger Tag soll immer oben stehen mit einer
  // Trennlinie Heute … hoch scrollen zu älteren Terminen" — die
  // „Heute"-Trennlinie wird an der chronologisch richtigen Stelle
  // eingefügt (auch wenn heute selbst keine Termine hat), und die Liste
  // scrollt beim Öffnen/Navigieren einmalig dorthin: ältere Termine des
  // bereits geladenen Zeitraums stehen dadurch oberhalb, erreichbar durch
  // Hochscrollen, statt dass man erst an allen künftigen Terminen
  // vorbeischrollen müsste, um sie zu sehen.
  const todayIso = isoDate(new Date());
  const showTodayDivider = todayIso >= from && todayIso <= to;
  const todayRef = useRef<HTMLDivElement>(null);

  type AgendaItem = { kind: 'today' } | { kind: 'day'; date: string };
  const items: AgendaItem[] = [];
  let insertedToday = false;
  for (const d of nonEmpty) {
    if (showTodayDivider && !insertedToday && d >= todayIso) {
      items.push({ kind: 'today' });
      insertedToday = true;
    }
    items.push({ kind: 'day', date: d });
  }
  if (showTodayDivider && !insertedToday) items.push({ kind: 'today' });

  // Einmalig je Zeitraum zur Trennlinie scrollen, sobald die echten Daten
  // geladen sind — nicht bei jeder Teilnehmer-Änderung durch eine
  // Rückmeldung, aber auch nicht zu früh: `AgendaList` rendert bereits
  // während `entries` noch lädt (`entriesByDate` dann eine leere `Map`,
  // `CalendarPage.tsx` zeigt „Lädt…" nur zusätzlich daneben, ersetzt diese
  // Liste nicht). In diesem Zustand bestünde `items` nur aus der
  // Trennlinie selbst (keine Tagesblöcke drumherum) — ein Scroll dorthin
  // wäre ein No-op, der aber fälschlich als „erledigt" markiert würde und
  // den echten, sinnvollen Scroll nach dem Nachladen verhindert (im Browser
  // so gefunden: ohne `isLoading`-Gate blieb die Liste beim Monatsanfang
  // stehen). Deshalb erst scrollen, wenn `!isLoading`.
  //
  // Zweiter, subtilerer Fallstrick (§ Nutzeranfrage „per Button auf diese
  // Seite … habe ich das aktuelle Datum immer noch nicht ganz oben … einmal
  // aktualisiere, steht es oben"): TanStack Router setzt bei **jeder**
  // Navigation selbst `window.scrollTo({ top: 0 })` zurück (eigener
  // „onRendered"-Event, unabhängig von der `scrollRestoration`-Option —
  // per `RouterCore.emit`/`Transitioner` im Router-Quellcode verifiziert,
  // `tanstack.com` war nicht erreichbar). Bei einem vollen Seiten-Reload
  // existiert dieser Event nicht (kein SPA-Navigationsvorgang), daher
  // funktionierte das Scrollen dort immer. Bei einer SPA-Navigation (Klick
  // auf „Kalender" in der Navigation) feuert der Router-eigene Reset aber
  // **asynchron und mit variabler Verzögerung** relativ zum Laden der
  // Kalender-Daten: Sind die Daten (React-Query-Cache) schon vorhanden
  // (z. B. beim zweiten Besuch der Seite in derselben Sitzung), lief unser
  // Scroll-Effekt bisher **vor** diesem Router-Reset — der Reset kam dann
  // als letztes Wort und setzte die Seite wieder auf 0 zurück. Beim ersten
  // Besuch (Daten noch nicht geladen) kam unser Scroll dagegen zufällig
  // **nach** dem Reset und „gewann" den Wettlauf — daher der scheinbar
  // zufällige Unterschied.
  //
  // Fix: zusätzlich auf das Router-eigene `onRendered`-Event abonnieren und,
  // falls für den aktuellen Zeitraum bereits (erfolgreich) gescrollt wurde,
  // den Scroll dort erneut anwenden — das korrigiert genau den Fall, dass
  // der Router-Reset NACH unserem Scroll kam. Bewusst **kein** Gate/Warten
  // auf dieses Event vor dem ersten Scroll: ein reiner Ansichtswechsel
  // (Kalender-Raster → Liste) ohne echte Navigation feuert „onRendered"
  // nicht erneut, da sonst nie gescrollt würde.
  const router = useRouter();
  const scrolledForRangeRef = useRef<string | null>(null);

  useEffect(() => {
    const rangeKey = `${from}|${to}`;
    if (isLoading || scrolledForRangeRef.current === rangeKey || !todayRef.current) return;
    todayRef.current.scrollIntoView({ block: 'start' });
    scrolledForRangeRef.current = rangeKey;
  });

  useEffect(
    () =>
      router.subscribe('onRendered', () => {
        const rangeKey = `${from}|${to}`;
        if (scrolledForRangeRef.current === rangeKey) todayRef.current?.scrollIntoView({ block: 'start' });
      }),
    [router, from, to],
  );

  if (items.length === 0) {
    return (
      <Card>
        <p className="text-sm text-slate-400">Keine Termine in diesem Zeitraum.</p>
      </Card>
    );
  }

  return (
    <Card>
      <div className="divide-y divide-slate-100">
        {items.map((item) => {
          if (item.kind === 'today') {
            return (
              <div key="today-divider" ref={todayRef} className="flex items-center gap-2 py-2">
                <span className="shrink-0 rounded-full bg-brand-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                  Heute
                </span>
                <div className="h-px flex-1 bg-brand-accent/30" />
              </div>
            );
          }
          const iso = item.date;
          return (
            <div key={iso} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
              <DateBadge date={iso} />
              {/* `min-w-0` here is load-bearing: without it this flex-1 column
               * refuses to shrink below its content's natural width, which
               * would push the row past the screen edge on narrow phones
               * instead of letting it wrap/shrink properly. */}
              <div className="min-w-0 flex-1 space-y-1.5">
                {entriesByDate.get(iso)!.map((e) => {
                  const struck = e.status === 'cancelled';
                  const sl = statusLabel(e.status);
                  // § Nutzeranfrage „Listenansicht … professionell und
                  // modern … immer noch unübersichtlich" — ein einzeiliger,
                  // mittenabgeschnittener Titel ("DJK VfL Willich 2 vs...")
                  // plus eine zweite, abgehängte Mannschafts-/Status-Gruppe
                  // weit rechts war schwer auf einen Blick zu erfassen.
                  // Jetzt: Uhrzeit fett/gut lesbar (statt winzig und blass),
                  // Titel bekommt seine eigene Zeile und darf umbrechen statt
                  // mitten im Wort abzuschneiden, Mannschaft/Halle als
                  // gedämpfte zweite Zeile direkt darunter statt rechts
                  // herausgelöst, und ein farbiger linker Akzentstreifen
                  // (`ENTRY_TYPE_BORDER`) macht den Termin-Typ auch beim
                  // schnellen Scrollen erkennbar — deutlich auffälliger als
                  // der vorherige 8px-Punkt.
                  //
                  // § Nutzeranfrage „angemeldet … grün schimmern … abgemeldet
                  // … rot": die Hintergrundfarbe der ganzen Zeile zeigt die
                  // eigene Rückmeldung, der linke Akzentstreifen bleibt
                  // unabhängig davon beim Termin-Typ.
                  const ownBg =
                    e.ownResponse === 'registered'
                      ? 'bg-green-50 hover:bg-green-100'
                      : e.ownResponse === 'declined'
                        ? 'bg-red-50 hover:bg-red-100'
                        : 'bg-slate-50 hover:bg-slate-100';
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => onPick(e)}
                      className={`flex w-full items-start gap-3 rounded-lg border-l-[3px] py-2 pl-3 pr-2.5 text-left transition-colors ${ownBg} ${ENTRY_TYPE_BORDER[e.type]}`}
                    >
                      <span className="w-10 shrink-0 pt-px text-sm font-semibold text-slate-600">
                        {e.startTime?.slice(0, 5) ?? '–'}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span
                            className={`text-sm font-medium ${struck ? 'text-slate-400 line-through' : 'text-slate-800'}`}
                          >
                            {e.title}
                          </span>
                          {sl && (
                            <span className="shrink-0 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-600">
                              {sl}
                            </span>
                          )}
                        </span>
                        {(e.teamName || e.venue) && (
                          <span className="mt-0.5 block truncate text-xs text-slate-500">
                            {[e.teamName, e.venue].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ── click-to-respond pop-ups (§ Nutzeranfrage) ──────────────────────────────
// Training reuses `TrainingRespondModal` (exported from TrainingPage.tsx) as-is
// — same data-fetching + `TrainingMenuModal` wrapper that `TrainingEventPage.tsx`
// (the per-training page, see docs/training.md) also uses, just fed this
// page's own `onChanged` to invalidate the calendar query after a response.

/** Same idea for Saisonspiele — `GET matches/:id`/`.../voting` are already
 * self-contained by id (no new backend endpoint needed, unlike Training).
 * The self-response row (Zusagen/Abmelden/Vielleicht) has no other home to
 * reuse from, since `RosterModal` itself has no "respond for yourself"
 * concept — it goes into `RosterModal`'s new `headerExtra` slot instead of
 * duplicating the whole grouped-list rendering a second time. */
function CalendarMatchPopup({
  matchId,
  onClose,
  onChanged,
}: {
  matchId: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const { theme } = useTheme();
  const qc = useQueryClient();

  const { data: m } = useQuery({
    queryKey: ['season', 'match', matchId],
    queryFn: () => api<SeasonMatchResponse>(`/season/matches/${matchId}`, { orgScoped: true }),
  });
  const { data: overview } = useQuery({
    queryKey: ['voting', matchId],
    queryFn: () => api<VotingOverview>(`/season/matches/${matchId}/voting`, { orgScoped: true }),
  });

  const invalidateLocal = () => {
    void qc.invalidateQueries({ queryKey: ['voting', matchId] });
    void qc.invalidateQueries({ queryKey: ['season', 'match', matchId] });
    onChanged();
  };

  const respond = useMutation({
    mutationFn: (v: { memberId: string; status: 'accepted' | 'declined' | 'maybe' }) =>
      api(`/season/matches/${matchId}/respond`, {
        method: 'POST',
        orgScoped: true,
        body: { memberId: v.memberId, status: v.status },
      }),
    onSuccess: invalidateLocal,
  });

  const reset = useMutation({
    mutationFn: (memberId: string) => api(`/season/matches/${matchId}/voting/${memberId}`, { method: 'DELETE', orgScoped: true }),
    onSuccess: invalidateLocal,
  });

  if (!m) return null;

  function respondWithCrossTeamWarning(memberId: string, teamIds: string[], status: 'accepted' | 'declined' | 'maybe') {
    if (
      teamIds.length > 0 &&
      m!.teamId &&
      !teamIds.includes(m!.teamId) &&
      !confirm(
        'Du gehörst nicht zur Mannschaft dieses Spiels — du meldest dich für eine andere Mannschaft an. Trotzdem fortfahren?',
      )
    ) {
      return;
    }
    respond.mutate({ memberId, status });
  }

  const participants = overview?.participants ?? [];
  const rosterGroups: RosterGroup[] = [
    {
      key: 'accepted',
      label: 'Zugesagt',
      icon: <span className="text-green-600">✓</span>,
      countClass: 'text-green-600',
      rowClass: 'bg-green-50',
      items: participants.filter((p) => p.status === 'accepted').map((p) => ({ id: p.memberId, name: p.name })),
    },
    {
      key: 'maybe',
      label: 'Unentschlossen',
      icon: <span className="text-amber-600">?</span>,
      countClass: 'text-amber-600',
      rowClass: 'bg-amber-50',
      items: participants.filter((p) => p.status === 'maybe').map((p) => ({ id: p.memberId, name: p.name })),
    },
    {
      key: 'declined',
      label: 'Abgesagt',
      icon: <span className="text-red-600">✕</span>,
      countClass: 'text-red-600',
      rowClass: 'bg-red-50',
      items: participants.filter((p) => p.status === 'declined').map((p) => ({ id: p.memberId, name: p.name })),
    },
  ];

  const showNames = m.respondableMembers.some((r) => !r.isOwn);
  const time = m.matchTime ? m.matchTime.slice(0, 5) : null;
  const fullDateLabel = m.matchDate
    ? new Date(`${m.matchDate}T00:00:00`).toLocaleDateString('de-DE', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : 'Termin offen';

  return (
    <RosterModal
      title={`${m.homeTeam ?? '—'} vs. ${m.awayTeam ?? '—'}`}
      subtitle={`${fullDateLabel}${time ? ` · ${time}` : ''}${m.venue ? ` · ${m.venue}` : ''}`}
      groups={rosterGroups}
      onClose={onClose}
      onReset={can('matches.manage') ? (memberId) => reset.mutate(memberId) : undefined}
      resettingId={reset.isPending ? (reset.variables ?? null) : null}
      headerExtra={
        m.respondableMembers.length > 0 ? (
          <div className="mb-4 space-y-1.5 rounded-md border border-slate-200 bg-slate-50 p-2.5">
            {m.respondableMembers.map((r) => {
              const isAccepted = r.status === 'accepted';
              const isDeclined = r.status === 'declined';
              const isMaybe = r.status === 'maybe';
              const dim = (active: boolean) => (r.status !== 'pending' && !active ? { opacity: 0.3 } : {});
              return (
                <div key={r.memberId} className="flex items-center gap-1">
                  {showNames && <span className="mr-1 max-w-24 truncate text-xs text-slate-500">{r.name}</span>}
                  <Button
                    variant="ghost"
                    title="Anmelden"
                    aria-label="Anmelden"
                    className="leading-none transition hover:brightness-95 active:brightness-90"
                    style={{ ...responseButtonStyle('accepted', isAccepted, theme), ...dim(isAccepted) }}
                    onClick={() => respondWithCrossTeamWarning(r.memberId, r.teamIds, 'accepted')}
                  >
                    <ThumbsUp size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    title="Abmelden"
                    aria-label="Abmelden"
                    className="leading-none transition hover:brightness-95 active:brightness-90"
                    style={{ ...responseButtonStyle('declined', isDeclined, theme), ...dim(isDeclined) }}
                    onClick={() => respondWithCrossTeamWarning(r.memberId, r.teamIds, 'declined')}
                  >
                    <ThumbsDown size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    title="Vielleicht"
                    aria-label="Vielleicht"
                    className="leading-none transition hover:brightness-95 active:brightness-90"
                    style={{ ...responseButtonStyle('maybe', isMaybe, theme), ...dim(isMaybe) }}
                    onClick={() => respondWithCrossTeamWarning(r.memberId, r.teamIds, 'maybe')}
                  >
                    <CircleHelp size={14} />
                  </Button>
                </div>
              );
            })}
          </div>
        ) : null
      }
    />
  );
}

// ── create / edit modal (Vereinstermine only — Saisonspiele/Training deep-link out) ──
function ClubEventEditor({
  eventId,
  teams,
  groups,
  onClose,
  onSaved,
}: {
  eventId?: string;
  teams: TeamResponse[];
  groups: TrainingGroupResponse[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!eventId;
  useLockBodyScroll();
  const { data: existing } = useQuery({
    queryKey: ['calendar', 'events', eventId],
    queryFn: () => api<ClubEventResponse>(`/calendar/events/${eventId}`, { orgScoped: true }),
    enabled: isEdit,
  });

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [venue, setVenue] = useState('');
  const [teamIdVal, setTeamIdVal] = useState('');
  const [groupIdVal, setGroupIdVal] = useState('');
  const [eventDate, setEventDate] = useState(isoDate(new Date()));
  const [startTime, setStartTime] = useState('18:00');
  const [endTime, setEndTime] = useState('19:00');
  const [repeats, setRepeats] = useState(false);
  const [frequency, setFrequency] = useState<'weekly' | 'interval'>('weekly');
  const [intervalWeeks, setIntervalWeeks] = useState(2);
  const [weekdays, setWeekdays] = useState<Set<number>>(new Set());
  const [endMode, setEndMode] = useState<'endDate' | 'count'>('count');
  const [endDate, setEndDate] = useState('');
  const [occurrenceCount, setOccurrenceCount] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  if (existing && !loaded) {
    setTitle(existing.title);
    setDescription(existing.description ?? '');
    setVenue(existing.venue ?? '');
    setTeamIdVal(existing.teamId ?? '');
    setGroupIdVal(existing.trainingGroupId ?? '');
    setEventDate(existing.eventDate);
    setStartTime(existing.startTime.slice(0, 5));
    setEndTime(existing.endTime.slice(0, 5));
    setLoaded(true);
  }

  const create = useMutation({
    mutationFn: async () => {
      const base = {
        title,
        description: description || undefined,
        venue: venue || undefined,
        teamId: teamIdVal || undefined,
        trainingGroupId: groupIdVal || undefined,
      };
      if (!repeats) {
        return api('/calendar/events', {
          method: 'POST',
          orgScoped: true,
          body: { ...base, eventDate, startTime, endTime },
        });
      }
      const series = await api<{ id: string }>('/calendar/series', {
        method: 'POST',
        orgScoped: true,
        body: {
          ...base,
          frequency,
          intervalWeeks: frequency === 'interval' ? intervalWeeks : 1,
          weekdays: [...weekdays],
          startDate: eventDate,
          endDate: endMode === 'endDate' ? endDate : undefined,
          occurrenceCount: endMode === 'count' ? occurrenceCount : undefined,
          startTime,
          endTime,
        },
      });
      return api(`/calendar/series/${series.id}/generate`, { method: 'POST', orgScoped: true, body: {} });
    },
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Termin konnte nicht gespeichert werden.'),
  });

  const update = useMutation({
    mutationFn: () =>
      api(`/calendar/events/${eventId}`, {
        method: 'PATCH',
        orgScoped: true,
        body: {
          title,
          description: description || null,
          venue: venue || null,
          teamId: teamIdVal || null,
          trainingGroupId: groupIdVal || null,
          eventDate,
          startTime,
          endTime,
        },
      }),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Termin konnte nicht gespeichert werden.'),
  });

  const remove = useMutation({
    mutationFn: () => api(`/calendar/events/${eventId}`, { method: 'DELETE', orgScoped: true }),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Termin konnte nicht gelöscht werden.'),
  });

  function toggleWeekday(d: number) {
    setWeekdays((prev) => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });
  }

  const canSubmit = title.trim().length > 0 && eventDate && startTime && endTime && (!repeats || weekdays.size > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-800">{isEdit ? 'Termin bearbeiten' : 'Termin anlegen'}</h2>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        {error && (
          <div className="mb-3">
            <Alert>{error}</Alert>
          </div>
        )}

        <div className="space-y-3">
          <Field label="Titel">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="z. B. Vorstandssitzung" />
          </Field>
          <Field label="Beschreibung (optional)">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
            />
          </Field>
          <Field label="Ort (optional)">
            <Input value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="z. B. Vereinsheim" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Mannschaft (optional)">
              <select
                value={teamIdVal}
                onChange={(e) => setTeamIdVal(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm text-slate-700"
              >
                <option value="">Ganzer Verein</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Trainingsgruppe (optional)">
              <select
                value={groupIdVal}
                onChange={(e) => setGroupIdVal(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm text-slate-700"
              >
                <option value="">Ganzer Verein</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {/* Stacked on narrow phones, 3-up from `sm:` — at 3 equal columns
           * on a ~375px screen, a native time input (now 16px to avoid the
           * iOS zoom-on-focus, see index.css) no longer fit its own value
           * ("06:00" clipped to "06:0"). */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label={repeats ? 'Beginnt am' : 'Datum'}>
              <Input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} />
            </Field>
            <Field label="Von">
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </Field>
            <Field label="Bis">
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </Field>
          </div>

          {!isEdit && (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={repeats} onChange={(e) => setRepeats(e.target.checked)} />
              Wiederholt sich (Serientermin)
            </label>
          )}

          {repeats && !isEdit && (
            <div className="space-y-3 rounded-md border border-slate-200 p-3">
              <Field label="Häufigkeit">
                <div className="flex items-center gap-2">
                  <select
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value as 'weekly' | 'interval')}
                    className="rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-700"
                  >
                    <option value="weekly">Jede Woche</option>
                    <option value="interval">Alle N Wochen</option>
                  </select>
                  {frequency === 'interval' && (
                    <Input
                      type="number"
                      min={1}
                      max={12}
                      value={intervalWeeks}
                      onChange={(e) => setIntervalWeeks(Number(e.target.value))}
                      className="w-20"
                    />
                  )}
                </div>
              </Field>
              <Field label="Wochentage">
                <div className="flex flex-wrap gap-1.5">
                  {WEEKDAY_LABELS.map((wd, i) => (
                    <button
                      key={wd}
                      type="button"
                      onClick={() => toggleWeekday(i + 1)}
                      className={`h-8 w-8 rounded-full text-xs font-medium ${
                        weekdays.has(i + 1) ? 'bg-brand-accent text-white' : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {wd}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Ende">
                <div className="flex items-center gap-3 text-sm">
                  <label className="flex items-center gap-1.5">
                    <input type="radio" checked={endMode === 'count'} onChange={() => setEndMode('count')} />
                    nach
                    <Input
                      type="number"
                      min={1}
                      max={400}
                      value={occurrenceCount}
                      onChange={(e) => setOccurrenceCount(Number(e.target.value))}
                      className="w-16"
                      disabled={endMode !== 'count'}
                    />
                    Terminen
                  </label>
                  <label className="flex items-center gap-1.5">
                    <input type="radio" checked={endMode === 'endDate'} onChange={() => setEndMode('endDate')} />
                    am
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      disabled={endMode !== 'endDate'}
                      className="w-36"
                    />
                  </label>
                </div>
              </Field>
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          {isEdit ? (
            <Button variant="danger" onClick={() => remove.mutate()} disabled={remove.isPending}>
              Löschen
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              onClick={() => (isEdit ? update.mutate() : create.mutate())}
              disabled={!canSubmit || create.isPending || update.isPending}
            >
              Speichern
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
